import React, { ComponentProps, memo } from "react";
import { usePreviewContext } from "../../contexts/preview-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useBottomMenu } from "../../hooks/useBottomMenu";
import { useDefinition } from "../../hooks/useDefinition";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useLabels } from "../../hooks/useLabels";
import { REMEMBER_NEXT_SPREADS_TOOLTIP } from "./constants";
import { labels } from "./labels";
import { NavigationBar } from "./navigation-bar";
import { useSpreadName } from "../../hooks/useSpreadName";
import { useSelectedSpread } from "../../hooks/useSelection";
import { useSpreadCount } from "../../hooks/useSpreadCount";

/** This will show a navigation bar when there are multiple spreads */
export const NavigationBarContainer = memo(() => {
	const { goTo } = usePreviewContext();
	const { googleAnalytics } = useAppContext();
	const currentSpread = useSelectedSpread();
	const totalNbrOfSpreads = useSpreadCount();
	const spreadIndex = currentSpread?.index ?? -1;
	const currentSpreadNbr = spreadIndex + 1;
	const navigationBarText = useSpreadName(currentSpread);
	const { stack } = useBottomMenu();

	const definition = useDefinition();
	const [isPreviewMode] = useIsPreviewMode();
	const isPreviewMandatory = definition?.product.isPreviewRequiredMC;
	const hideNextSpreadsTooltip = sessionStorage.getItem(REMEMBER_NEXT_SPREADS_TOOLTIP);

	const [[checkNextSpreadTooltip]] = useLabels([labels.nextSpreadsTooltip]);

	// Don't navigation bar if not multi-spread
	if (totalNbrOfSpreads <= 1) return null;

	// When in edit mode, don't show navigation
	if (stack.find((item) => item.id === "text-value")) return null;

	const onChangeSpreadNbr: ComponentProps<typeof NavigationBar>["onChangeSpreadNbr"] = (
		newSpreadNr,
	) => {
		const delta = goTo(newSpreadNr - 1);
		if (isPreviewMode && isPreviewMandatory) {
			sessionStorage.setItem(REMEMBER_NEXT_SPREADS_TOOLTIP, "true");
		}
		if (delta < 0) {
			googleAnalytics.trackEventToGa({
				trackingAction: "PrevSpreadClicked",
			});
		} else if (delta > 0) {
			googleAnalytics.trackEventToGa({
				trackingAction: "NextSpreadClicked",
			});
		}
	};

	const onClickTooltip = () => {
		sessionStorage.setItem(REMEMBER_NEXT_SPREADS_TOOLTIP, "true");
	};

	const tooltTipText =
		isPreviewMode && isPreviewMandatory && !hideNextSpreadsTooltip
			? checkNextSpreadTooltip
			: "";

	return (
		<NavigationBar
			currentSpreadNbr={currentSpreadNbr}
			totalNbrOfSpreads={totalNbrOfSpreads}
			navigationBarText={navigationBarText}
			onChangeSpreadNbr={onChangeSpreadNbr}
			toolTipRightText={tooltTipText}
			onToolTipRightClick={onClickTooltip}
		/>
	);
});
NavigationBarContainer.displayName = "NavigationBarContainer";
